import React, { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Clock, Award, ChevronDown, ChevronUp, Send, AlertTriangle } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;

const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` } });

const RATINGS = ["Exceeds Expectations","Meets Expectations","Needs Improvement","Unacceptable"];
const STATUS_STEPS = ["Pending","Self-Submitted","Manager-Reviewed","HR-Finalized"];

export default function EmployeePerformance() {
  const { T } = useTheme();
  const RATING_COLOR = {
    "Exceeds Expectations": T.green,
    "Meets Expectations":   T.cyan,
    "Needs Improvement":    T.amber,
    "Unacceptable":         T.red,
  };
  const userData   = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const platformId = userData.id || userData.user_id || null;

  const [appraisals, setAppraisals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [goals,      setGoals]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selApp,     setSelApp]     = useState(null);
  const location   = useLocation();
  const tabFromPath = () => location.pathname.includes("/goals") ? "goals" : "appraisals";
  const [mainTab,    setMainTab]    = useState(tabFromPath);
  const [submitGoalId, setSubmitGoalId] = useState(null);
  const [submitNote,   setSubmitNote]   = useState("");
  const [submittingGoal, setSubmittingGoal] = useState(false);
  const [progressMap,  setProgressMap]  = useState({});
  const [responses,  setResponses]  = useState({});  // {question_id: {self_evaluation, self_rating}}
  const [expanded,   setExpanded]   = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState("");

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3500); };

  const load = async () => {
    setLoading(true);
    try {
      const [aRes, catRes, gRes] = await Promise.all([
        axios.get(`${API}/performance/my-appraisals?platform_user_id=${platformId}`, ax()),
        axios.get(`${API}/performance/categories?platform_user_id=${platformId}`, ax()),
        axios.get(`${API}/performance/my-goals?platform_user_id=${platformId}`, ax()),
      ]);
      setAppraisals(aRes.data  || []);
      setCategories(catRes.data|| []);
      setGoals(gRes.data       || []);
      if ((aRes.data||[]).length > 0 && !selApp) setSelApp(aRes.data[0]);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (platformId) load(); }, [platformId]);
  useEffect(() => { setMainTab(tabFromPath()); }, [location.pathname]);

  // Init responses from existing data when appraisal changes
  useEffect(() => {
    if (!selApp) return;
    const init = {};
    (selApp.responses||[]).forEach(r => {
      init[r.question_id] = {
        self_evaluation: r.self_evaluation || "",
        self_rating:     r.self_rating     || "",
      };
    });
    setResponses(init);
    // Expand all categories by default
    const exp = {};
    categories.forEach(c => { exp[c.id] = true; });
    setExpanded(exp);
  }, [selApp?.id]);

  const setResponse = (qid, field, val) => {
    setResponses(p => ({ ...p, [qid]: { ...(p[qid]||{}), [field]: val } }));
  };

  const submitSelf = async () => {
    if (!selApp) return;
    // Validate at least one response filled
    const filled = Object.values(responses).filter(r => r.self_evaluation || r.self_rating);
    if (filled.length === 0) { flash("❌ Please fill at least one response"); return; }

    setSubmitting(true);
    try {
      const payload = {
        responses: categories.flatMap(cat =>
          cat.questions.map(q => ({
            question_id:     q.id,
            self_evaluation: (responses[q.id]||{}).self_evaluation || "",
            self_rating:     (responses[q.id]||{}).self_rating     || "",
          }))
        ).filter(r => r.self_evaluation || r.self_rating),
      };

      await axios.post(
        `${API}/performance/appraisals/${selApp.id}/self-submit?platform_user_id=${platformId}`,
        payload, ax()
      );
      flash("✅ Self-appraisal submitted successfully!");
      load();
    } catch(e) { flash("❌ " + (e.response?.data?.detail || "Error submitting")); }
    finally { setSubmitting(false); }
  };

  const canEdit = selApp && ["Pending","Self-Submitted"].includes(selApp.status);
  const stepIdx = selApp ? STATUS_STEPS.indexOf(selApp.status) : -1;

  if (loading) return <div style={{ padding:40, textAlign:"center", color:T.muted, fontFamily:"Outfit,sans-serif" }}>Loading…</div>;

  if (mainTab === "goals") {
    const S_COLOR = { "Not Started":T.muted,"In Progress":T.cyan,"Submitted":T.amber,"Completed":T.green };
    const P_COLOR = { High:T.red, Medium:T.amber, Low:T.green };
    return (
      <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>
        {/* same header already rendered above */}
        {goals.length === 0 ? (
          <div style={{ padding:60, textAlign:"center", color:T.muted }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🎯</div>
            <div style={{ fontSize:16, fontWeight:600, color:T.text, marginBottom:8 }}>No Goals Assigned Yet</div>
            <div style={{ fontSize:14 }}>Your manager or HR will assign goals to you.</div>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
            {goals.map(g => {
              const isSubmittable = ["Not Started","In Progress"].includes(g.status);
              const prog = progressMap[g.id] !== undefined ? progressMap[g.id] : g.progress;
              return (
                <div key={g.id} style={{ background:T.card, border:`1px solid ${g.status==="Submitted"?`${T.amber}33`:T.border}`, borderRadius:12, padding:20 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:15, fontWeight:600, color:T.text, marginBottom:4 }}>{g.title}</div>
                      {g.description && <div style={{ fontSize:12.5, color:T.muted, marginBottom:6 }}>{g.description}</div>}
                      {g.kpi_metric && <div style={{ fontSize:12, color:T.dim }}>KPI: <span style={{ color:T.muted }}>{g.kpi_metric}</span></div>}
                    </div>
                    <span style={{ fontSize:11.5, padding:"3px 10px", borderRadius:20, fontWeight:600, flexShrink:0, marginLeft:12,
                      background:`${S_COLOR[g.status]||T.muted}26`, color:S_COLOR[g.status]||T.muted }}>
                      {g.status}
                    </span>
                  </div>

                  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:12 }}>
                    <span style={{ fontSize:11, padding:"2px 7px", borderRadius:8, background:`${P_COLOR[g.priority]||T.amber}26`, color:P_COLOR[g.priority]||T.amber }}>{g.priority}</span>
                    <span style={{ fontSize:11, padding:"2px 7px", borderRadius:8, background:`${T.purple}1A`, color:T.purple }}>{g.category}</span>
                    {g.due_date && <span style={{ fontSize:11, color:T.dim }}>Due: {g.due_date}</span>}
                  </div>

                  {/* Progress bar + slider */}
                  <div style={{ marginBottom:isSubmittable?10:0 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:4 }}>
                      <span style={{ color:T.muted }}>Progress</span>
                      <span style={{ color:T.cyan, fontFamily:"JetBrains Mono,monospace", fontWeight:700 }}>{prog}%</span>
                    </div>
                    <div style={{ height:6, background:T.surface_med, borderRadius:3, overflow:"hidden", marginBottom:isSubmittable?6:0 }}>
                      <div style={{ height:"100%", width:`${prog}%`, background:`linear-gradient(90deg,${T.cyan},${T.green})`, borderRadius:3, transition:"width 0.2s" }}/>
                    </div>
                    {isSubmittable && (
                      <input type="range" min={0} max={100} value={prog}
                        onChange={async (e) => {
                          const val = parseInt(e.target.value);
                          setProgressMap(p => ({...p,[g.id]:val}));
                          try { await axios.put(`${API}/performance/goals/${g.id}/progress?platform_user_id=${platformId}`, { progress:val }, ax()); }
                          catch(err) { console.error(err); }
                        }}
                        style={{ width:"100%", accentColor:T.cyan }}/>
                    )}
                  </div>

                  {/* Submit button */}
                  {isSubmittable && (
                    <div style={{ marginTop:10 }}>
                      {submitGoalId === g.id ? (
                        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                          <textarea value={submitNote} onChange={e=>setSubmitNote(e.target.value)} rows={2}
                            placeholder="Describe what you achieved…"
                            style={{ width:"100%", background:T.inp_bg, border:`1px solid ${T.border}`, borderRadius:8, color:T.text, fontSize:13, padding:"8px 12px", outline:"none", fontFamily:"Outfit,sans-serif", resize:"vertical", boxSizing:"border-box" }}/>
                          <div style={{ display:"flex", gap:8 }}>
                            <button onClick={()=>setSubmitGoalId(null)} style={{ padding:"7px 14px", borderRadius:8, border:`1px solid ${T.toggle_bg}`, background:"none", color:T.muted, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12 }}>Cancel</button>
                            <button disabled={submittingGoal} onClick={async()=>{
                              setSubmittingGoal(true);
                              try {
                                await axios.put(`${API}/performance/goals/${g.id}/submit?platform_user_id=${platformId}&completion_note=${encodeURIComponent(submitNote)}`, {}, ax());
                                setSubmitGoalId(null); setSubmitNote(""); load();
                              } catch(err) { alert(err.response?.data?.detail||"Error"); }
                              finally { setSubmittingGoal(false); }
                            }} style={{ flex:1, padding:"7px 14px", borderRadius:8, border:"none", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, color:"#fff", cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700, opacity:submittingGoal?0.7:1 }}>
                              {submittingGoal?"Submitting…":"Submit for Approval"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={()=>{ setSubmitGoalId(g.id); setSubmitNote(""); }}
                          style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 14px", borderRadius:8, border:`1px solid ${T.green}40`, background:`${T.green}12`, color:T.green, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:12.5, fontWeight:600 }}>
                          ✓ Mark Complete & Submit
                        </button>
                      )}
                    </div>
                  )}

                  {g.completion_note && (
                    <div style={{ marginTop:8, padding:"8px 12px", background:`${T.amber}0D`, borderRadius:8, border:`1px solid ${T.amber}1A`, fontSize:12.5, color:T.amber }}>
                      Your note: {g.completion_note}
                    </div>
                  )}
                  {g.manager_note && (
                    <div style={{ marginTop:6, padding:"8px 12px", background:`${T.green}0D`, borderRadius:8, border:`1px solid ${T.green}1A`, fontSize:12.5, color:T.green }}>
                      Manager: {g.manager_note}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {toast && (
          <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
            background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
            color:toast.startsWith("✅")?T.green:T.red, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
            {toast}
          </div>
        )}
      </div>
    );
  }

  if (appraisals.length === 0) return (
    <div style={{ padding:60, textAlign:"center", fontFamily:"Outfit,sans-serif", color:T.muted }}>
      <Award size={48} style={{ color:T.dim, marginBottom:16 }}/>
      <div style={{ fontSize:18, fontWeight:600, color:T.text, marginBottom:8 }}>No Appraisals Yet</div>
      <div style={{ fontSize:14 }}>HR hasn't started an appraisal cycle for you yet.</div>
    </div>
  );

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, color:T.cyan, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>PERFORMANCE</div>
        <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px" }}>My Performance</h2>
        <p style={{ fontSize:13, color:T.muted, margin:0 }}>Your appraisals, goals and KPIs</p>
      </div>

      {/* Main tabs */}
      <div style={{ display:"flex", gap:0, marginBottom:20, background:T.card, borderRadius:10, border:`1px solid ${T.border}`, overflow:"hidden" }}>
        {[
          { id:"appraisals", label:"My Appraisals", count:appraisals.length },
          { id:"goals",      label:"My Goals & KPIs", count:goals.filter(g=>g.status!=="Completed").length },
        ].map(({ id, label, count }) => (
          <button key={id} onClick={() => setMainTab(id)}
            style={{ flex:1, padding:"11px 16px", fontSize:13, fontWeight:500, fontFamily:"Outfit,sans-serif",
              color:mainTab===id?T.cyan:T.muted, background:mainTab===id?`${T.cyan}14`:"none",
              border:"none", borderBottom:mainTab===id?`2px solid ${T.cyan}`:"2px solid transparent",
              cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
            {label}
            {count > 0 && <span style={{ fontSize:10, padding:"1px 7px", borderRadius:10,
              background:mainTab===id?`${T.cyan}26`:T.toggle_bg,
              color:mainTab===id?T.cyan:T.muted, fontFamily:"JetBrains Mono,monospace" }}>{count}</span>}
          </button>
        ))}
      </div>

      {/* Cycle selector */}
      {appraisals.length > 1 && (
        <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
          {appraisals.map(a => (
            <button key={a.id} onClick={() => setSelApp(a)}
              style={{ padding:"8px 16px", borderRadius:8, cursor:"pointer", fontFamily:"Outfit,sans-serif", fontSize:13,
                border:`1px solid ${selApp?.id===a.id?`${T.cyan}4D`:T.toggle_bg}`,
                background:selApp?.id===a.id?`${T.cyan}14`:T.surface_faint,
                color:selApp?.id===a.id?T.cyan:T.muted }}>
              {a.cycle_name}
            </button>
          ))}
        </div>
      )}

      {selApp && (
        <>
          {/* Progress bar */}
          <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:"16px 20px", marginBottom:20 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div>
                <div style={{ fontSize:15, fontWeight:600 }}>{selApp.cycle_name}</div>
                <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>{selApp.review_period}</div>
              </div>
              <span style={{ fontSize:12, padding:"3px 10px", borderRadius:20, fontWeight:600,
                background:selApp.status==="HR-Finalized"?`${T.green}1A`:`${T.amber}1A`,
                color:selApp.status==="HR-Finalized"?T.green:T.amber }}>
                {selApp.status.replace("-"," ")}
              </span>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
              {STATUS_STEPS.map((step, i) => (
                <div key={step} style={{ textAlign:"center" }}>
                  <div style={{ width:32, height:32, borderRadius:"50%", margin:"0 auto 6px",
                    background: i<=stepIdx?T.cyan:T.surface_soft,
                    border:`2px solid ${i<=stepIdx?T.cyan:T.hover_border}`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    color: i<=stepIdx?"#fff":T.dim, fontWeight:700, fontSize:13 }}>
                    {i < stepIdx ? "✓" : i+1}
                  </div>
                  <div style={{ fontSize:10.5, color:i<=stepIdx?T.cyan:T.dim, textAlign:"center" }}>
                    {step.replace("-"," ")}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Final outcome if HR finalized */}
          {selApp.status === "HR-Finalized" && selApp.overall_rating && (
            <div style={{ background:`${T.green}0F`, border:`1px solid ${T.green}33`, borderRadius:12, padding:"16px 20px", marginBottom:20, display:"flex", gap:24, flexWrap:"wrap" }}>
              <div>
                <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>Final Rating</div>
                <div style={{ fontSize:15, fontWeight:700, color:RATING_COLOR[selApp.overall_rating]||T.green, marginTop:4 }}>{selApp.overall_rating}</div>
              </div>
              <div>
                <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>Increment</div>
                <div style={{ fontSize:15, fontWeight:700, color:T.amber, marginTop:4 }}>{selApp.increment_percent}%</div>
              </div>
              <div>
                <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>New Annual CTC</div>
                <div style={{ fontSize:15, fontWeight:700, color:T.green, marginTop:4, fontFamily:"JetBrains Mono,monospace" }}>
                  ₹{Math.round(selApp.new_ctc||0).toLocaleString("en-IN")}
                </div>
              </div>
              {selApp.hr_notes && (
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:10.5, color:T.dim, textTransform:"uppercase", letterSpacing:"0.08em" }}>HR Notes</div>
                  <div style={{ fontSize:13, color:T.muted, marginTop:4 }}>{selApp.hr_notes}</div>
                </div>
              )}
            </div>
          )}

          {/* Self-appraisal form */}
          {categories.map(cat => (
            <div key={cat.id} style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:12, marginBottom:14, overflow:"hidden" }}>
              <div style={{ padding:"14px 20px", display:"flex", justifyContent:"space-between", alignItems:"center",
                cursor:"pointer", borderBottom:expanded[cat.id]?`1px solid ${T.border}`:"none",
                background:T.surface_faint }}
                onClick={() => setExpanded(p => ({...p,[cat.id]:!p[cat.id]}))}>
                <div>
                  <span style={{ fontSize:14, fontWeight:700, color:T.text }}>{cat.name}</span>
                  <span style={{ fontSize:11, marginLeft:8, padding:"2px 8px", borderRadius:8,
                    background:`${T.purple}1A`, color:T.purple }}>{cat.type}</span>
                </div>
                {expanded[cat.id] ? <ChevronUp size={15} style={{ color:T.muted }}/> : <ChevronDown size={15} style={{ color:T.muted }}/>}
              </div>

              {expanded[cat.id] && cat.questions.map((q, qi) => {
                const resp = responses[q.id] || {};
                // Find manager response
                const managerResp = (selApp.responses||[]).find(r => r.question_id === q.id);
                return (
                  <div key={q.id} style={{ padding:"18px 20px", borderBottom:qi<cat.questions.length-1?`1px solid ${T.toggle_bg}`:"none" }}>
                    <div style={{ fontSize:13.5, fontWeight:600, color:T.text, marginBottom:4 }}>{q.title}</div>
                    {q.description && <div style={{ fontSize:12, color:T.muted, marginBottom:12 }}>{q.description}</div>}

                    <div style={{ display:"grid", gridTemplateColumns: selApp.status==="Manager-Reviewed"||selApp.status==="HR-Finalized" ? "1fr 1fr" : "1fr", gap:16 }}>
                      {/* Self Evaluation */}
                      <div>
                        <div style={{ fontSize:11, color:T.cyan, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:6 }}>Your Evaluation</div>
                        <textarea
                          value={resp.self_evaluation||""}
                          onChange={e => setResponse(q.id,"self_evaluation",e.target.value)}
                          disabled={!canEdit}
                          rows={3}
                          placeholder={canEdit ? "Describe your performance, achievements and contributions…" : "Not filled"}
                          style={{ width:"100%", background:canEdit?T.inp_bg:T.surface_faint,
                            border:`1px solid ${T.border}`, borderRadius:8, color:T.text,
                            fontSize:13, padding:"9px 12px", outline:"none", fontFamily:"Outfit,sans-serif",
                            resize:"vertical", boxSizing:"border-box", opacity:canEdit?1:0.7 }}/>
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:8 }}>
                          {RATINGS.map(r => (
                            <button key={r} onClick={() => canEdit && setResponse(q.id,"self_rating",r)} disabled={!canEdit}
                              style={{ fontSize:11, padding:"4px 10px", borderRadius:7, cursor:canEdit?"pointer":"default",
                                border:`1px solid ${resp.self_rating===r?RATING_COLOR[r]||T.cyan:T.toggle_bg}`,
                                background:resp.self_rating===r?`${RATING_COLOR[r]||T.cyan}26`:T.surface_faint,
                                color:resp.self_rating===r?RATING_COLOR[r]||T.cyan:T.muted,
                                fontFamily:"Outfit,sans-serif", transition:"all 0.15s" }}>
                              {r}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Manager Evaluation */}
                      {(selApp.status==="Manager-Reviewed"||selApp.status==="HR-Finalized") && managerResp && (
                        <div style={{ background:`${T.purple}0A`, border:`1px solid ${T.purple}1A`, borderRadius:9, padding:14 }}>
                          <div style={{ fontSize:11, color:T.purple, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>
                            Manager Evaluation — {selApp.manager_name}
                          </div>
                          <div style={{ fontSize:13, color:T.text, lineHeight:1.6 }}>
                            {managerResp.manager_evaluation || <em style={{ color:T.dim }}>—</em>}
                          </div>
                          {managerResp.manager_rating && (
                            <div style={{ marginTop:8 }}>
                              <span style={{ fontSize:11, padding:"3px 9px", borderRadius:8,
                                background:`${RATING_COLOR[managerResp.manager_rating]||T.muted}26`,
                                color:RATING_COLOR[managerResp.manager_rating]||T.muted }}>
                                {managerResp.manager_rating}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

          {/* Submit button */}
          {canEdit && (
            <div style={{ display:"flex", justifyContent:"flex-end", marginTop:16, gap:12, alignItems:"center" }}>
              <div style={{ fontSize:12.5, color:T.muted }}>
                You can save and come back — submit when ready.
              </div>
              <button onClick={submitSelf} disabled={submitting}
                style={{ display:"flex", alignItems:"center", gap:8, padding:"11px 24px", borderRadius:9,
                  border:"none", background:`linear-gradient(135deg,${T.cyan},${T.purple})`, color:"#fff",
                  fontFamily:"Outfit,sans-serif", fontSize:14, fontWeight:700, cursor:"pointer",
                  opacity:submitting?0.7:1 }}>
                <Send size={14}/>{submitting?"Submitting…":"Submit Self-Appraisal"}
              </button>
            </div>
          )}

          {/* Areas for improvement from manager */}
          {selApp.improvements?.length > 0 && (
            <div style={{ background:`${T.amber}0F`, border:`1px solid ${T.amber}26`, borderRadius:12, padding:"16px 20px", marginTop:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:T.amber, marginBottom:10 }}>
                <AlertTriangle size={14} style={{ display:"inline", marginRight:6, verticalAlign:"middle" }}/>
                Areas for Improvement (Manager Feedback)
              </div>
              {selApp.improvements.map(i => (
                <div key={i.id} style={{ fontSize:13, color:T.text, padding:"6px 0", borderBottom:`1px solid ${T.amber}14` }}>
                  • {i.text}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%", transform:"translateX(-50%)", padding:"10px 22px", borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card, border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red, boxShadow:T.shadow, fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
}